// PrivacyPolicy.jsx
// Styled Privacy Policy page using React + Tailwind CSS
// Similar look-and-feel as Terms & Conditions

import React from "react";

const SECTIONS = [
  { id: "intro", title: "Introduction" },
  { id: "information", title: "Information We Collect" },
  { id: "collection-methods", title: "How We Collect Information" },
  { id: "use", title: "Use of Your Personal Information" },
  { id: "cookies", title: "Cookies" },
  { id: "updates", title: "Policy Updates" },
  { id: "contact", title: "Contact Information" },
];

export default function PrivacyPolicy() {
  return (
    <main className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 text-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-2 text-white/90">Rankridge Junior College</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-lime-300" />
            Last Updated: 2024
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-10 lg:grid-cols-[280px_1fr]">
        {/* TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              On this page
            </p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block rounded-lg px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-teal-700"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Policy Content */}
        <article className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
          <Section id="intro" title="Introduction">
            This Privacy Policy sets out how Rankridge uses and protects any information that you give while you use this
            website. We are committed to ensuring that your privacy is protected. All information collected is subject to
            this Privacy Policy 2024.
          </Section>

          <Section id="information" title="Information We Collect">
            <ul className="list-disc ps-5">
              <li><strong>Contact Information:</strong> name, email, mobile, phone, address, country, IP address.</li>
              <li><strong>Information You Post:</strong> posts on our website or third‑party social media.</li>
              <li><strong>Demographic Information:</strong> details you provide during surveys or website use.</li>
              <li><strong>Other Information:</strong> IP address, browser, site activity, mobile device type, OS version.</li>
            </ul>
          </Section>

          <Section id="collection-methods" title="How We Collect Information">
            <ul className="list-disc ps-5">
              <li><strong>Directly from you:</strong> forms, applications, inquiries, comments, calls, or emails.</li>
              <li><strong>Passively:</strong> via tools like Google Analytics, cookies, and security trackers.</li>
              <li><strong>From third parties:</strong> details shared by social media or partner sites.</li>
            </ul>
          </Section>

          <Section id="use" title="Use of Your Personal Information">
            <ul className="list-disc ps-5">
              <li>To contact you, respond to inquiries, or confirm course applications.</li>
              <li>To improve admission services and personalize your experience.</li>
              <li>To analyze site trends and improve services.</li>
              <li>For security purposes, marketing, and transactional communications.</li>
              <li>As otherwise permitted by law.</li>
            </ul>
          </Section>

          <Section id="cookies" title="Cookies">
            Cookies are small files stored on your device. We use them to analyze web traffic, tailor the website to your
            needs, and improve performance. Cookies do not give us access to your computer or private information, other
            than data you choose to share. You can accept or decline cookies in your browser settings.
          </Section>

          <Section id="updates" title="Policy Updates">
            We reserve the right to modify any or all terms of this Privacy Policy at any time. Please check this page
            periodically to review the current version.
          </Section>

          <Section id="contact" title="Contact Information">
            For questions or suggestions regarding this policy, please contact us at:
            <ul className="mt-2 list-disc ps-5">
              <li>Website: rankridge.com</li>
              <li>Email: info@rankridge.com</li>
            </ul>
            We may also use your details to communicate via SMS, Email, WhatsApp, or Voice (with your consent).
          </Section>

          <hr className="my-8" />
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-sm text-neutral-500">© {new Date().getFullYear()} Rankridge Junior College. All rights reserved.</p>
            <a
              href="#top"
              className="rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Back to top
            </a>
          </div>
        </article>
      </div>
    </main>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-neutral-900">
        {title}
      </h2>
      <div className="prose mt-3 max-w-none text-neutral-700 prose-p:leading-relaxed prose-li:leading-relaxed">
        {typeof children === "string" ? <p>{children}</p> : children}
      </div>
      <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
    </section>
  );
}
